import { axiosRequest, type AxiosResult } from "@/lib/axios";

const API_BASE_URL = import.meta.env.VITE_SERVICE_CORE_URL;

export interface VideoUploadUrlResponse {
	upload_url: string;
	upload_id: string;
}

export const videoUploadApi = {
	/**
	 * Get a Mux direct upload URL for video upload
	 * The upload_url can be used to upload the video directly to Mux
	 * The webhook will automatically update the review when video is ready
	 */
	getUploadUrl: (workspaceId: string, reviewId: string): Promise<AxiosResult<VideoUploadUrlResponse>> => {
		return axiosRequest({
			url: `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}/video`,
			method: "POST",
		});
	},

	/**
	 * Upload video file directly to Mux using the provided upload URL
	 * Returns a promise that resolves when upload is complete
	 */
	uploadToMux: (uploadUrl: string, file: File, onProgress?: (percent: number) => void): Promise<void> => {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();

			xhr.upload.addEventListener("progress", (event) => {
				if (event.lengthComputable && onProgress) {
					const percent = Math.round((event.loaded / event.total) * 100);
					onProgress(percent);
				}
			});

			xhr.addEventListener("load", () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					resolve();
				} else {
					reject(new Error("Upload failed. Please try again."));
				}
			});

			xhr.addEventListener("error", () => {
				reject(new Error("Network error during upload. Please check your connection and try again."));
			});

			xhr.addEventListener("abort", () => {
				reject(new Error("Upload cancelled."));
			});

			xhr.open("PUT", uploadUrl);
			xhr.setRequestHeader("Content-Type", file.type);
			xhr.send(file);
		});
	},
};
